import cn from 'classnames';
import { type MutableRefObject, useImperativeHandle, useLayoutEffect, useMemo } from 'react';
import { observeElementOffset, observeElementRect, useVirtualizer } from '@tanstack/react-virtual';

import type { BaseRowData } from '@adv-frontend/data-provider';

import type { GridHandle } from '../../../types';
import { BodyBackground } from '../BodyBackground/BodyBackground';
import { LoadMoreRowsRowWrapper } from '../LoadMoreRowsRowWrapper/LoadMoreRowsRowWrapper';
import { Row } from '../Row/Row';

import type { RowVirtualizer, VirtualBodyProps } from './VirtualBody.types';

import styles from '../Grid.module.css';

const isJSDOM = globalThis?.navigator?.userAgent?.includes('jsdom/') ?? false;
const fakeObserver = () => () => {};

type VirtualBodyContentProps<RowData extends BaseRowData> = VirtualBodyProps<RowData> & {
    mode: 'element' | 'window-keep-mounted';
    rowVirtualizer: RowVirtualizer;
    commitRows?: (indexes: readonly number[]) => void;
    bodyRef?: MutableRefObject<HTMLDivElement | null>;
    scrollMargin?: number;
    isReady?: boolean;
};

/**
 * Виртуализаторы строк и столбцов должны запускать эффекты в одном компоненте:
 * смена их порядка меняет первые замеры строк и начальное горизонтальное смещение.
 */
export function useVirtualBodyContent<RowData extends BaseRowData>(props: VirtualBodyContentProps<RowData>) {
    const {
        cellSize = 'm',
        dataProvider,
        grid,
        gridForwardRef,
        gridHeaderRef,
        gridRef,
        rows,
        rowClassName,
        rowHighlight,
        scrollableContainerClassName,
        selectionManager,
        verticalScrollDirection,
        virtualization,
        hideRowBorderBottom,
        onRowClick,
        mode,
        rowVirtualizer,
        commitRows,
        bodyRef,
        scrollMargin = 0,
        isReady = true,
    } = props;
    const isWindowKeepMounted = mode === 'window-keep-mounted';
    const {
        scrollToIndex: scrollToRowIndex,
        scrollToOffset: scrollToRowOffset,
        getTotalSize: getTotalHeight,
        getVirtualItems: getVirtualRows,
        measureElement: measureRow,
    } = rowVirtualizer;

    // Виртуализируем только незапиненные столбцы.
    const centerColumns = grid.getCenterVisibleLeafColumns();
    const {
        getVirtualItems: getVirtualColumns,
        measure: updateVirtualColumns,
        scrollToIndex: scrollToCellIndex,
        scrollToOffset: scrollToCellOffset,
        // eslint-disable-next-line react-hooks/incompatible-library -- Виртуализатор управляет диапазоном столбцов.
    } = useVirtualizer({
        horizontal: true,
        initialRect: virtualization.initialRect,
        overscan: 3,
        getScrollElement: () => gridRef.current,
        count: centerColumns.length,
        // Стейт таблицы содержит точные размеры столбцов, поэтому дополнительное измерение не требуется.
        estimateSize: index => centerColumns[index]?.getSize() ?? 0,
        // Положение столбцов рассчитывается относительно левой запиненной области.
        paddingStart: grid.getLeftTotalSize(),
        // В jsdom наблюдатели не работают, поэтому виртуализированный контент ориентируется на initialRect.
        observeElementRect: isJSDOM ? fakeObserver : observeElementRect,
        observeElementOffset: isJSDOM ? fakeObserver : observeElementOffset,
    });

    const { columnOrder, columnPinning, columnSizing, columnVisibility } = grid.getState();
    useLayoutEffect(() => {
        updateVirtualColumns();
    }, [columnOrder, columnPinning, columnSizing, columnVisibility, updateVirtualColumns]);

    const totalHeight = getTotalHeight();
    const totalWidth = grid.getTotalSize();

    // До первого замера сохраняем полную оценочную высоту без монтажа строк в неверных координатах.
    const measuredVirtualRows = isReady ? getVirtualRows() : [];
    const virtualRows = isWindowKeepMounted
        ? measuredVirtualRows.map(row => ({
              ...row,
              start: row.start - scrollMargin,
              end: row.end - scrollMargin,
          }))
        : measuredVirtualRows;
    const virtualColumns = getVirtualColumns();

    useLayoutEffect(() => {
        // Удерживаем только строки, которые дошли до завершённого React-коммита.
        commitRows?.(virtualRows.map(row => row.index));
    }, [commitRows, virtualRows]);

    const visibleRange = rowVirtualizer.range;
    const overscan = virtualization.overscan ?? 1;
    // Сохранённые строки могут образовывать разрозненные диапазоны, поэтому фон строится по текущему viewport.
    const backgroundVirtualRows = isWindowKeepMounted
        ? virtualRows.filter(
              row =>
                  visibleRange !== null &&
                  row.index >= visibleRange.startIndex - overscan &&
                  row.index <= visibleRange.endIndex + overscan
          )
        : virtualRows;

    const bodyStyles = useMemo(
        () => ({ height: `${totalHeight}px`, width: `${totalWidth}px` }),
        [totalWidth, totalHeight]
    );

    /** Добавляем гриду дополнительные методы. */
    useImperativeHandle<GridHandle, GridHandle>(
        gridForwardRef,
        () => ({
            scrollCellToIndex: (index, options) => scrollToCellIndex(index, options),
            scrollRowToIndex: (index, options) => scrollToRowIndex(index, options),
            scrollToOffset: (offset, options) =>
                scrollToRowOffset(isWindowKeepMounted ? offset + scrollMargin : offset, options),
            getFirstVisibleRowIndex: () => {
                if (isWindowKeepMounted) {
                    const scrollTop = (gridRef.current?.ownerDocument.defaultView?.scrollY ?? 0) - scrollMargin;
                    return (
                        virtualRows.find(row => row.end > scrollTop)?.index ??
                        virtualRows[virtualRows.length - 1]?.index ??
                        0
                    );
                }

                let visibleRow = virtualRows[0];
                const gridScrollTop = gridRef.current?.scrollTop;

                if (virtualization.overscan !== undefined) {
                    for (let index = 0; index <= virtualization.overscan; index++) {
                        const rowStart = virtualRows[index]?.start;
                        const rowEnd = virtualRows[index]?.end;

                        if (
                            gridScrollTop !== undefined &&
                            rowStart !== undefined &&
                            rowEnd &&
                            gridScrollTop >= rowStart &&
                            gridScrollTop <= rowEnd
                        ) {
                            visibleRow = virtualRows[index];
                        }
                    }
                }

                return visibleRow?.index ?? 0;
            },
            horizontalScrollToOffset: offset => {
                scrollToCellOffset(offset);
            },
            isScrollableByY: () => {
                if (isWindowKeepMounted) {
                    const ownerWindow = gridRef.current?.ownerDocument.defaultView;
                    return Boolean(
                        ownerWindow && ownerWindow.document.documentElement.scrollHeight > ownerWindow.innerHeight
                    );
                }

                if (gridHeaderRef.current?.clientHeight && gridRef.current?.clientHeight) {
                    return gridHeaderRef.current.clientHeight + totalHeight > gridRef.current.clientHeight;
                }

                return false;
            },
            isScrolledToEndByY: () => {
                if (isWindowKeepMounted) {
                    const element = gridRef.current;
                    const ownerWindow = element?.ownerDocument.defaultView;
                    return Boolean(
                        element && ownerWindow && element.getBoundingClientRect().bottom <= ownerWindow.innerHeight
                    );
                }

                if (gridRef.current?.offsetHeight && gridRef.current?.scrollTop && gridRef.current?.scrollHeight) {
                    return gridRef.current.offsetHeight + gridRef.current.scrollTop >= gridRef.current.scrollHeight;
                }

                return false;
            },
            getGridDimensions: () => ({ height: totalHeight, width: totalWidth }),
        }),
        [
            gridHeaderRef,
            gridRef,
            isWindowKeepMounted,
            scrollMargin,
            scrollToCellIndex,
            scrollToCellOffset,
            scrollToRowIndex,
            scrollToRowOffset,
            totalHeight,
            totalWidth,
            virtualRows,
            virtualization.overscan,
        ]
    );

    return (
        <div
            ref={bodyRef}
            className={cn(styles.body, scrollableContainerClassName ? scrollableContainerClassName : undefined)}
            style={bodyStyles}
        >
            <BodyBackground
                cellSize={cellSize}
                grid={grid}
                virtualization={virtualization}
                virtualRows={backgroundVirtualRows}
                verticalScrollDirection={verticalScrollDirection}
            >
                {virtualRows.map(virtualRow => {
                    const row = rows[virtualRow.index];
                    if (!row) {
                        return null;
                    }

                    if (row.original.type === 'loadMore' && props.loadMoreRowsType === 'onClick') {
                        return (
                            <LoadMoreRowsRowWrapper<RowData>
                                key={row.id}
                                row={row}
                                virtualRow={virtualRow}
                                chunkSize={props.chunkSize}
                                renderLoadMoreRow={props.renderLoadMoreRow}
                                grid={grid}
                                measure={measureRow}
                                hideBottomBorder={hideRowBorderBottom}
                                onLoadMore={props.onLoadMore}
                                totalCount={props.totalCount}
                                loadedCount={props.loadedCount}
                                subRowsTotalCount={props.subRowsTotalCount}
                                subRowsLoadedCount={props.subRowsLoadedCount}
                                virtualColumns={virtualColumns}
                            />
                        );
                    }

                    return (
                        <Row
                            key={row.id}
                            className={rowClassName}
                            dataProvider={dataProvider}
                            grid={grid}
                            row={row}
                            rowHighlight={rowHighlight}
                            virtualRow={virtualRow}
                            virtualColumns={virtualColumns}
                            measure={measureRow}
                            scrollableRef={gridRef}
                            selectionManager={selectionManager}
                            hideBottomBorder={hideRowBorderBottom}
                            strictHeight={
                                'strictSize' in virtualization
                                    ? virtualization.strictSize(virtualRow.index) + 1
                                    : undefined
                            }
                            onClick={onRowClick}
                        />
                    );
                })}
            </BodyBackground>
        </div>
    );
}
